import { NavLink, Outlet, Navigate, useLocation } from "react-router-dom";
import { Component } from "react";
import { LayoutDashboard, Settings, ListTree, FileText, Briefcase, Factory, ShieldCheck, BarChart3, HelpCircle, Newspaper, FileDown, Image, Images, Inbox, Search, Users } from "lucide-react";
import { useAuth } from "./AuthContext";
import { Toaster } from "../components/ui/sonner";
import "./admin.css";

const NAV = [
  { grp: "Overview" },
  { to: "/admin", icon: <LayoutDashboard />, label: "Dashboard", end: true },
  { grp: "Site" },
  { to: "/admin/settings", icon: <Settings />, label: "Site Settings" },
  { to: "/admin/navigation", icon: <ListTree />, label: "Navigation" },
  { to: "/admin/pages", icon: <FileText />, label: "Pages" },
  { to: "/admin/seo", icon: <Search />, label: "SEO" },
  { grp: "Content" },
  { to: "/admin/services", icon: <Briefcase />, label: "Services" },
  { to: "/admin/industries", icon: <Factory />, label: "Industries" },
  { to: "/admin/compliance", icon: <ShieldCheck />, label: "Compliance" },
  { to: "/admin/stats", icon: <BarChart3 />, label: "Stats" },
  { to: "/admin/faqs", icon: <HelpCircle />, label: "FAQs" },
  { grp: "Resources" },
  { to: "/admin/news", icon: <Newspaper />, label: "Insights & News" },
  { to: "/admin/publications", icon: <FileDown />, label: "Publications" },
  { to: "/admin/gallery", icon: <Images />, label: "Gallery" },
  { to: "/admin/media", icon: <Image />, label: "Media" },
  { grp: "Inbox & Access" },
  { to: "/admin/submissions", icon: <Inbox />, label: "Submissions" },
  { to: "/admin/users", icon: <Users />, label: "Users & Account" },
];

class AdminErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  render() {
    if (this.state.error) {
      return (
        <div className="adm-card" data-testid="admin-error-fallback">
          <h2>Something went wrong on this page</h2>
          <p style={{ color: "var(--muted, #5a5e78)", margin: "10px 0 0" }}>
            The rest of the admin panel is unaffected — use the sidebar to navigate. ({String(this.state.error?.message || this.state.error)})
          </p>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const location = useLocation();

  if (user === null) return <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", fontFamily: "Inter" }}>Loading…</div>;
  if (user === false) return <Navigate to="/admin/login" state={{ from: location }} replace />;

  return (
    <div className="adm" data-testid="admin-layout">
      <aside className="adm-side">
        <div className="adm-brand">
          <img src="/lemur-logo.png" alt="Lemur Quality" />
          <div>
            <b>Lemur Quality</b>
            <small>Admin panel</small>
          </div>
        </div>
        <nav className="adm-nav">
          {NAV.map((n, i) =>
            n.grp ? (
              <span className="grp" key={`g${i}`}>{n.grp}</span>
            ) : (
              <NavLink key={n.to} to={n.to} end={n.end} className={({ isActive }) => (isActive ? "active" : "")}
                data-testid={`admin-nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                {n.icon} {n.label}
              </NavLink>
            )
          )}
        </nav>
        <div className="adm-side-foot">
          <div>{user.email}</div>
          <div className="mono">{user.role}</div>
          <button onClick={logout} data-testid="admin-logout">Sign out</button>
        </div>
      </aside>
      <main className="adm-main">
        <AdminErrorBoundary key={location.pathname}>
          <Outlet />
        </AdminErrorBoundary>
      </main>
      <Toaster position="top-right" richColors />
    </div>
  );
}
